// src/components/Recouverement/RecouvrementListPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Card,
  CardBody,
  Row,
  Col,
  Button,
  Input,
  Label,
  Table,
  Badge,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Spinner,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { FaFileExcel, FaFilter, FaSearch, FaTimes } from "react-icons/fa";
import { recouvrementActions } from "../../sagas/recouvrementSlice";
import { clientActions } from "../../sagas/clientSlice";

/* =========================
 * Utils
 * ========================= */
const fmtMoney = (n) => {
  const x = Number(n || 0);
  return x.toLocaleString("fr-MA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};
const iso = (d) => (d ? new Date(d).toISOString().slice(0, 10) : null);
const startOfMonthISO = () => {
  const now = new Date();
  return iso(new Date(now.getFullYear(), now.getMonth(), 1));
};
const endOfMonthISO = () => {
  const now = new Date();
  return iso(new Date(now.getFullYear(), now.getMonth() + 1, 0));
};
const isOverdueISO = (dateISO) => {
  if (!dateISO) return false;
  const t = new Date().toISOString().slice(0, 10);
  return dateISO < t;
};
const safeStr = (v) => (v == null ? "" : String(v));

const RecouvrementListPage = () => {
  const dispatch = useDispatch();

  const {
    // data
    summary,
    details,
    selectedDate,

    // ui
    loadingSummary,
    loadingDetails,
    exporting,
    error,

    // store filters (fallback)
    dateDebut,
    dateFin,
    clientId,
    banque,
    query,
  } = useSelector((s) => s.recouvrement);

  const clientsState = useSelector((s) => s.clients || s.client || {});
  const clients = clientsState?.clients || clientsState?.list || [];

  /* ===== Local filters ===== */
  const [fStart, setFStart] = useState(dateDebut || startOfMonthISO());
  const [fEnd, setFEnd] = useState(dateFin || endOfMonthISO());
  const [fClientId, setFClientId] = useState(clientId ?? "");
  const [fBanque, setFBanque] = useState(banque ?? "");
  const [fQuery, setFQuery] = useState(query ?? "");

  // ✅ NEW: status filter (null => tous)
  // values: null | "EN_ATTENTE" | "ENCAISSE" | "ANNULE" | "CAUTION"
  const [fStatut, setFStatut] = useState("EN_ATTENTE");

  // drawer
  const [q, setQ] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);

  /* ===== Load clients (optional) ===== */
  useEffect(() => {
    dispatch(clientActions.findAll({ page: 0, size: 9999 }));
  }, [dispatch]);

  const fetchSummary = (override = {}) => {
    const params = {
      dateDebut: override.dateDebut ?? fStart,
      dateFin: override.dateFin ?? fEnd,
      clientId:
        override.clientId !== undefined
          ? override.clientId
          : fClientId
          ? Number(fClientId)
          : null,
      banque: override.banque ?? (fBanque ? fBanque : null),
      query: override.query ?? (fQuery ? fQuery : null),
      statut: override.statut !== undefined ? override.statut : fStatut, // ✅
    };

    dispatch(recouvrementActions.fetchSummary(params));
  };

  /* ===== Fetch summary on mount ===== */
  useEffect(() => {
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = () => fetchSummary();

  const resetFilters = () => {
    const s = startOfMonthISO();
    const e = endOfMonthISO();
    setFStart(s);
    setFEnd(e);
    setFClientId("");
    setFBanque("");
    setFQuery("");
    setFStatut("EN_ATTENTE");
    setQ("");

    fetchSummary({
      dateDebut: s,
      dateFin: e,
      clientId: null,
      banque: null,
      query: null,
      statut: "EN_ATTENTE",
    });
  };

  const openDrawer = (dateEcheance) => {
    setQ("");
    setDrawerOpen(true);

    dispatch(
      recouvrementActions.fetchDetails({
        dateEcheance,
        clientId: fClientId ? Number(fClientId) : null,
        banque: fBanque || null,
        query: fQuery || null,
        statut: fStatut === "" ? null : fStatut, // ✅ null = tous
      })
    );
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    dispatch(recouvrementActions.clearDetails());
  };

  const exportExcel = () => {
    dispatch(
      recouvrementActions.exportExcel({
        dateDebut: fStart,
        dateFin: fEnd,
        clientId: fClientId ? Number(fClientId) : null,
        banque: fBanque || null,
        query: fQuery || null,
        statut: fStatut === "" ? null : fStatut, // ✅
      })
    );
  };

  const totals = useMemo(() => {
    const arr = summary || [];
    const totalCount = arr.reduce((acc, x) => acc + Number(x?.count || 0), 0);
    const totalAmount = arr.reduce((acc, x) => acc + Number(x?.total || 0), 0);
    return { totalCount, totalAmount };
  }, [summary]);

  const filteredDetails = useMemo(() => {
    const qq = (q || "").trim().toLowerCase();
    if (!qq) return details || [];
    return (details || []).filter((d) => {
      const hay = [
        d?.nomClient,
        d?.codeClient,
        d?.numeroCheque,
        d?.reference,
        d?.numFactures,
        d?.numBonLivraisons,
        d?.banque,
        d?.referencePaiement,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(qq);
    });
  }, [details, q]);

  const drawerTotals = useMemo(() => {
    const arr = filteredDetails || [];
    const totalAmount = arr.reduce((acc, x) => acc + Number(x?.montantRegle || 0), 0);
    return { count: arr.length, totalAmount };
  }, [filteredDetails]);

  const renderClientLabel = (c) => {
    if (!c) return "";
    const code = c.code ? `${c.code} — ` : "";
    const name = c.entreprise || `${safeStr(c.nom)} ${safeStr(c.prenom)}`.trim();
    return `${code}${name}`.trim();
  };

  const statutLabel = useMemo(() => {
    if (fStatut === null || fStatut === "") return "TOUS";
    return fStatut;
  }, [fStatut]);

  return (
    <Container fluid className="py-3">
      <Row className="g-3">
        {/* ========================= Filters ========================= */}
        <Col xs={12}>
          <Card className="border-0 shadow-sm" style={{ borderRadius: 16 }}>
            <CardBody style={{ padding: 14 }}>
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="d-flex align-items-center gap-2">
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 12,
                      display: "grid",
                      placeItems: "center",
                      background: "#F1F5F9",
                    }}
                  >
                    <FaFilter />
                  </div>
                  <div>
                    <div style={{ fontWeight: 900, fontSize: 16, color: "#0F172A" }}>
                      Recouvrement — Liste
                    </div>
                    <div style={{ fontSize: 12, color: "#64748B" }}>
                      Chèques par date d’échéance · Statut : <strong>{statutLabel}</strong>
                    </div>
                  </div>
                </div>

                <Button
                  color="link"
                  className="p-0"
                  onClick={resetFilters}
                  title="Réinitialiser"
                  style={{ color: "#64748B" }}
                >
                  <FaTimes />
                </Button>
              </div>

              <Row className="g-2 align-items-end">
                <Col xs={12} md={2}>
                  <Label className="mb-1" style={{ fontSize: 12, color: "#475569" }}>
                    Du
                  </Label>
                  <Input type="date" value={fStart || ""} onChange={(e) => setFStart(e.target.value)} bsSize="sm" />
                </Col>

                <Col xs={12} md={2}>
                  <Label className="mb-1" style={{ fontSize: 12, color: "#475569" }}>
                    Au
                  </Label>
                  <Input type="date" value={fEnd || ""} onChange={(e) => setFEnd(e.target.value)} bsSize="sm" />
                </Col>

                <Col xs={12} md={3}>
                  <Label className="mb-1" style={{ fontSize: 12, color: "#475569" }}>
                    Client
                  </Label>
                  <Input type="select" value={fClientId} onChange={(e) => setFClientId(e.target.value)} bsSize="sm">
                    <option value="">Tous les clients</option>
                    {(clients || []).map((c) => (
                      <option key={c.id} value={c.id}>
                        {renderClientLabel(c)}
                      </option>
                    ))}
                  </Input>
                </Col>

                {/* ✅ NEW status filter */}
                <Col xs={12} md={2}>
                  <Label className="mb-1" style={{ fontSize: 12, color: "#475569" }}>
                    Statut
                  </Label>
                  <Input
                    type="select"
                    value={fStatut === null ? "" : fStatut}
                    onChange={(e) => setFStatut(e.target.value === "" ? null : e.target.value)}
                    bsSize="sm"
                  >
                    <option value="">TOUS (EN_ATTENTE + ENCAISSE…)</option>
                    <option value="EN_ATTENTE">EN_ATTENTE (à encaisser)</option>
                    <option value="ENCAISSE">ENCAISSE</option>
                    <option value="ANNULE">ANNULE</option>
                    <option value="CAUTION">CAUTION</option>
                  </Input>
                </Col>

                

                <Col xs={12} md={3} className="d-flex gap-2 mt-1">
                  <Button
                    color="primary"
                    style={{ flex: 1, borderRadius: 12, fontWeight: 900 }}
                    onClick={applyFilters}
                    disabled={loadingSummary}
                  >
                    {loadingSummary ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Chargement…
                      </>
                    ) : (
                      "Appliquer"
                    )}
                  </Button>

                  <Button
                    color="success"
                    style={{ borderRadius: 12, fontWeight: 900 }}
                    onClick={exportExcel}
                    disabled={exporting}
                    title="Exporter Excel"
                  >
                    {exporting ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Export…
                      </>
                    ) : (
                      <>
                        <FaFileExcel className="me-2" />
                        Excel
                      </>
                    )}
                  </Button>
                </Col>
                {error ? (
                  <Col xs={12}>
                    <div
                      style={{
                        marginTop: 10,
                        padding: 10,
                        borderRadius: 12,
                        background: "#FEF2F2",
                        color: "#991B1B",
                        fontSize: 13,
                      }}
                    >
                      {error}
                    </div>
                  </Col>
                ) : null}
              </Row>
            </CardBody>
          </Card>
        </Col>

        {/* ========================= Summary table ========================= */}
        <Col xs={12}>
          <Card className="border-0 shadow-sm" style={{ borderRadius: 16 }}>
            <CardBody>
              <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                <div>
                  <div style={{ fontWeight: 900, fontSize: 16, color: "#0F172A" }}>
                    Chèques par date d’échéance
                  </div>
                  <div style={{ fontSize: 12, color: "#64748B" }}>
                    Cliquez sur un <strong>montant</strong> pour ouvrir les détails
                  </div>
                </div>

                <Badge
                  color="light"
                  style={{
                    color: "#0F172A",
                    borderRadius: 999,
                    padding: "8px 12px",
                    border: "1px solid #E2E8F0",
                  }}
                >
                  Période : <strong className="ms-1">{fStart || "—"}</strong> →{" "}
                  <strong>{fEnd || "—"}</strong> · Statut : <strong>{statutLabel}</strong>
                </Badge>
              </div>

              <div style={{ border: "1px solid #E2E8F0", borderRadius: 14, overflow: "hidden" }}>
                <Table responsive hover className="mb-0 align-middle">
                  <thead style={{ background: "#F8FAFC" }}>
                    <tr>
                      <th style={{ fontSize: 12, color: "#475569" }}>Date échéance</th>
                      <th className="text-end" style={{ fontSize: 12, color: "#475569" }}>
                        Nb chèques
                      </th>
                      <th className="text-end" style={{ fontSize: 12, color: "#475569" }}>
                        Total
                      </th>
                      <th style={{ width: 70 }} />
                    </tr>
                  </thead>

                  <tbody>
                    {loadingSummary ? (
                      <tr>
                        <td colSpan="4" className="text-center py-5 text-muted">
                          <Spinner /> <span className="ms-2">Chargement…</span>
                        </td>
                      </tr>
                    ) : !summary || summary.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="text-center py-5 text-muted">
                          Aucun résultat pour cette période.
                        </td>
                      </tr>
                    ) : (
                      summary.map((r, idx) => {
                        const dateISO = r.dateEcheance;
                        const overdue = isOverdueISO(dateISO) && Number(r.total || 0) > 0;

                        // si statut = EN_ATTENTE, on affiche le badge "Échu"
                        const showOverdueBadge = (fStatut === "EN_ATTENTE" || fStatut == null) && overdue;

                        return (
                          <tr
                            key={`${dateISO || "row"}-${idx}`}
                            onClick={() => openDrawer(dateISO)}
                            style={{ cursor: "pointer" }}
                            title="Voir détails"
                          >
                            <td style={{ fontWeight: 800, color: "#0F172A" }}>
                              {dateISO || "—"}
                              {showOverdueBadge ? (
                                <Badge color="warning" pill className="ms-2" style={{ fontSize: 11 }}>
                                  Échu
                                </Badge>
                              ) : null}
                            </td>

                            <td className="text-end">
                              <Badge color="secondary" pill>
                                {r.count || 0}
                              </Badge>
                            </td>

                            <td className="text-end" style={{ fontWeight: 900, color: "#0F172A" }}>
                              {fmtMoney(r.total)} MAD
                            </td>

                            <td className="text-end" style={{ color: "#64748B" }}>
                              ›
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>

                  {summary && summary.length > 0 ? (
                    <tfoot style={{ background: "#F8FAFC" }}>
                      <tr>
                        <td style={{ fontWeight: 900 }}>TOTAL</td>
                        <td className="text-end" style={{ fontWeight: 900 }}>
                          {totals.totalCount}
                        </td>
                        <td className="text-end" style={{ fontWeight: 900 }}>
                          {fmtMoney(totals.totalAmount)} MAD
                        </td>
                        <td />
                      </tr>
                    </tfoot>
                  ) : null}
                </Table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* ========================= Drawer details ========================= */}
      <Offcanvas
        isOpen={drawerOpen}
        toggle={closeDrawer}
        direction="end"
        style={{
          width: "min(520px, 92vw)",
          borderTopLeftRadius: 16,
          borderBottomLeftRadius: 16,
        }}
      >
        <OffcanvasHeader toggle={closeDrawer}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 900, fontSize: 16, color: "#0F172A" }}>Détails chèques</div>
            <div style={{ fontSize: 12, color: "#64748B" }}>
              Échéance : <strong>{selectedDate || "—"}</strong> · Statut :{" "}
              <strong>{statutLabel}</strong>
            </div>
          </div>
        </OffcanvasHeader>

        <OffcanvasBody>
          <div className="d-flex gap-2 align-items-center mb-3">
            <div style={{ position: "relative", flex: 1 }}>
              <FaSearch style={{ position: "absolute", left: 12, top: 12, color: "#94A3B8" }} />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Rechercher: client, n° chèque, facture/BL…"
                style={{ paddingLeft: 36, borderRadius: 12 }}
              />
            </div>
          </div>

          <div
            className="d-flex align-items-center justify-content-between mb-3"
            style={{
              padding: "10px 12px",
              borderRadius: 14,
              border: "1px solid #E2E8F0",
              background: "#F8FAFC",
            }}
          >
            <div style={{ fontSize: 12, color: "#475569" }}>
              <strong>{drawerTotals.count}</strong> chèque(s)
            </div>
            <div style={{ fontSize: 12, color: "#475569" }}>
              Total: <strong>{fmtMoney(drawerTotals.totalAmount)} MAD</strong>
            </div>
          </div>

          {loadingDetails ? (
            <div className="text-center py-5 text-muted">
              <Spinner /> <span className="ms-2">Chargement…</span>
            </div>
          ) : !filteredDetails || filteredDetails.length === 0 ? (
            <div className="text-center py-5 text-muted">Aucun détail.</div>
          ) : (
            <div className="d-grid gap-2">
              {filteredDetails.map((p) => (
                <div
                  key={p.id}
                  style={{
                    border: "1px solid #E2E8F0",
                    borderRadius: 14,
                    padding: 12,
                    background: "white",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 900, color: "#0F172A" }}>
                        {p.nomClient || "Client"}
                        {p.codeClient ? (
                          <span style={{ fontWeight: 700, color: "#64748B" }}> — {p.codeClient}</span>
                        ) : null}
                      </div>

                      <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>
                        Chèque: <strong>{p.numeroCheque || "—"}</strong>
                        {p.banque ? (
                          <span>
                            {" "}
                            · Banque: <strong>{p.banque}</strong>
                          </span>
                        ) : null}
                      </div>

                      <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>
                        Facture(s): <strong>{p.numFactures || "—"}</strong> · BL:{" "}
                        <strong>{p.numBonLivraisons || "—"}</strong>
                      </div>
                    </div>

                    <div className="text-end">
                      <div style={{ fontWeight: 900, fontSize: 16, color: "#0F172A" }}>
                        {fmtMoney(p.montantRegle)} MAD
                      </div>
                      <Badge
                        color={p.statut === "EN_ATTENTE" ? "warning" : p.statut === "ENCAISSE" ? "success" : "secondary"}
                        pill
                      >
                        {p.statut}
                      </Badge>
                    </div>
                  </div>

                  <div className="mt-2 d-flex flex-wrap gap-2">
                    <Badge color="light" style={{ color: "#0F172A", border: "1px solid #E2E8F0" }}>
                      Réf: <strong>{p.reference || "—"}</strong>
                    </Badge>
                    <Badge color="light" style={{ color: "#0F172A", border: "1px solid #E2E8F0" }}>
                      Échéance: <strong>{p.dateEcheance || "—"}</strong>
                    </Badge>
                    {p.dateReglement ? (
                      <Badge color="light" style={{ color: "#0F172A", border: "1px solid #E2E8F0" }}>
                        Date règlement: <strong>{p.dateReglement}</strong>
                      </Badge>
                    ) : null}
                  </div>

                  {p.commentaire ? (
                    <div style={{ marginTop: 8, fontSize: 12, color: "#475569" }}>
                      {p.commentaire}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}

          <div className="mt-3 d-flex gap-2">
            <Button
              color="success"
              style={{ borderRadius: 12, fontWeight: 900, flex: 1 }}
              onClick={exportExcel}
              disabled={exporting}
              title="Exporter Excel (mêmes filtres)"
            >
              {exporting ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Export…
                </>
              ) : (
                <>
                  <FaFileExcel className="me-2" />
                  Export Excel
                </>
              )}
            </Button>

            <Button color="light" style={{ borderRadius: 12, fontWeight: 900 }} onClick={closeDrawer}>
              Fermer
            </Button>
          </div>
        </OffcanvasBody>
      </Offcanvas>
    </Container>
  );
};

export default RecouvrementListPage;